<template>
  <div class="slots-cont" :class="{ loading }">
    <div v-if="!loading" class="d-flex flex-wrap">
      <div
        v-for="slot in slots"
        :key="slot.scheduled_time"
        class="slot-button"
        :data-test-id="`time-slot-start-${slot.start_hour}`"
        :class="{
          disabled: slot.is_in_past,
          selected: isSelected(slot),
          available: !slot.disabled,
          unavailable: slot.disabled
        }"
        :title="getTooltip(slot)"
        :data-cy="`time-slot-${slot.scheduled_time}`"
        @click="handleClick(slot)"
      >
        {{ getSlotLabel(slot) }}
        <i
          v-if="isSelected(slot)"
          class="fas fa-check-circle selected-icon"
        ></i>
      </div>
    </div>
    <div v-else class="time-slots-loading">
      <Spinner message="Loading availability..." />
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";

import Spinner from "@/components/Spinner.vue";
import { customDateFormatWithTimezone } from "../mixins";

export default {
  components: {
    Spinner
  },
  props: {
    timeSlots: {
      type: Array,
      default: () => []
    },
    modelValue: {
      type: Object,
      default: () => ({})
    },
    timezone: {
      type: String,
      default: "America/Chicago"
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "unavailable", "warning"],
  computed: {
    slots() {
      return this.timeSlots.map((slot) => {
        const isPastTime =
          moment(slot.scheduled_time).isBefore(moment(), "day") ||
          (moment(slot.scheduled_time).isSame(moment(), "day") &&
            slot.start_hour < moment().hour());
        return {
          ...slot,
          is_in_past: isPastTime
        };
      });
    }
  },
  methods: {
    getSlotLabel(slot) {
      return `${customDateFormatWithTimezone(
        slot.arrival_window_start,
        "h"
      )} - ${customDateFormatWithTimezone(slot.arrival_window_end, "h a")}`;
    },
    getTooltip(slot) {
      if (slot.is_in_past) {
        return "Cannot schedule in the past.";
      }
      return slot.disabled ? "Unavailable" : "Available";
    },
    handleClick(slot) {
      if (!slot.is_in_past) {
        this.$emit("update:modelValue", {
          scheduled_time: slot.scheduled_time,
          arrival_window_start: slot.arrival_window_start,
          arrival_window_end: slot.arrival_window_end
        });
        this.$emit("unavailable", slot.disabled);
      }
      if (slot.disabled) {
        this.$emit("warning", "Unavailable Times Are Not Auto Assigned");
      } else {
        this.$emit("warning", "");
      }
    },
    isSelected(slot) {
      return this.modelValue?.scheduled_time === slot.scheduled_time;
    }
  }
};
</script>

<style scoped lang="scss">
.slots-cont {
  position: relative;
  min-height: 225px;
  .loading {
    opacity: 0.5;
  }
}
.slot-button {
  margin: 0 5px 5px 0;
  border: 2px solid var(--darkblue);
  background: #fff;
  color: var(--darkblue);
  border-radius: 15px;
  padding: 5px;
  text-align: center;
  font-size: 0.9em;
  white-space: nowrap;
  flex: 0 1 23%;
  cursor: pointer;
  position: relative;
  &:nth-child(4n) {
    margin-right: 0;
  }
  &.available {
    color: var(--white);
    background: var(--organic-green);
    border-color: var(--organic-green);
    &.selected {
      border-color: var(--pakistan-green);
    }
  }
  &.unavailable {
    background: var(--yellow-crayola);
    border-color: var(--yellow-crayola);
    color: var(--font-color);
    &.selected {
      border-color: var(--gold);
    }
    .selected-icon {
      color: var(--gold);
    }
  }
  &.disabled {
    border-color: var(--light-color);
    color: var(--secondary-color);
    background: var(--light-color);
    opacity: 0.6;
  }
  &.selected {
    font-weight: bold;
  }
  .selected-icon {
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    color: var(--pakistan-green);
    font-size: 1.2em;
    background: radial-gradient(#fff 50%, transparent 50%);
  }
}
.time-slots-loading {
  position: absolute;
  background: #fff;
  color: var(--darkblue);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 15px;
  border-radius: 3px;
  box-shadow: 0 3px 6px -2px #9ca4aa;
}
</style>
